import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets-cm19k8pv.emergentagent.net/job_9eb43c1b-4d2e-4271-9c32-e74ba97db8fa/artifacts/gl7n6sbc_AdobeStock_2175036770b.webp";
const CRAFT_IMAGE = "https://customer-assets.emergentagent.com/job_9eb43c1b-4d2e-4271-9c32-e74ba97db8fa/artifacts/zoaizra1_Alien_Tenome4.jpg";

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/products?featured=true`);
        // Filter only sculptures (exclude T-shirts) for home page
        const sculptures = response.data.filter(p => !p.name.includes('T-Shirt'));
        setFeaturedProducts(sculptures.slice(0, 6));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter/subscribe`, null, { params: { email } });
      setSubscribeStatus('success');
      setEmail('');
    } catch (error) {
      setSubscribeStatus('error');
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white pt-24 md:pt-0">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${HERO_IMAGE})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>

        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-neutral-300 uppercase tracking-[0.2em] md:tracking-[0.3em] text-xs md:text-sm mb-4 md:mb-6">
              Premium Art Collection
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight text-white"
          >
            Bring your favourite
            <br />
            <span className="text-[#e84628]">worlds</span> to life!
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-4 md:mt-8 text-neutral-200 text-base md:text-xl max-w-2xl mx-auto px-4 md:px-0"
          >
            Handcrafted 3D printed sculptures capturing iconic moments 
            from your favorite movies and series.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-6 md:mt-12 flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4 md:px-0"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="btn-primary inline-flex items-center justify-center gap-2 py-3 md:py-4"
            >
              <span className="hidden sm:inline">Explore Collection</span>
              <span className="sm:hidden">Shop Now</span>
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-about-button"
              className="px-8 py-3 md:py-4 border-2 border-white text-white font-semibold uppercase tracking-wider hover:bg-white hover:text-black transition-all text-center"
            >
              Our Story
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown size={32} className="animate-bounce text-neutral-400" />
        </motion.div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
          >
            <div>
              <span className="text-neutral-400 uppercase tracking-[0.2em] text-sm">
                Collection
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mt-2 text-black">
                Featured Pieces
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-link"
              className="text-neutral-500 hover:text-black uppercase tracking-wider text-sm font-semibold flex items-center gap-2 transition-colors"
            >
              View All
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {featuredProducts.map((product, index) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Custom Request Banner */}
      <section className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_9eb43c1b-4d2e-4271-9c32-e74ba97db8fa/artifacts/yg2nclp7_TenomeBanner2.jpg"
          alt="Can't find it? We can make it! Got an idea? Let's make it real."
          className="w-full h-auto object-cover"
        />
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-neutral-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-neutral-400 uppercase tracking-[0.2em] text-sm">
                Our Craft
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mt-2 text-black">
                Where Art
                <br />
                Meets Cinema
              </h2>
              <p className="mt-8 text-neutral-500 text-lg leading-relaxed">
                Each Tenome sculpture is a labor of love, meticulously designed and 
                3D printed to capture the essence of cinema's most memorable moments. 
                From the raw power of sci-fi to the subtle emotions of drama, our 
                pieces transform iconic scenes into timeless art.
              </p>
              <p className="mt-6 text-neutral-500 text-lg leading-relaxed">
                We believe in quality over quantity. Every piece goes through multiple 
                stages of refinement before it's ready to become part of your collection.
              </p>
              <Link
                to="/about"
                data-testid="about-link"
                className="inline-flex items-center gap-2 mt-8 text-black uppercase tracking-wider text-sm font-semibold hover:gap-4 transition-all"
              >
                Learn More About Us
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-square bg-black overflow-hidden">
                <img
                  src={CRAFT_IMAGE}
                  alt="Tenome Art"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 md:py-32 bg-white border-t border-neutral-200">
        <div className="container-custom max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-neutral-400 uppercase tracking-[0.2em] text-sm">
              Stay Updated
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mt-2 text-black">
              Join The List
            </h2>
            <p className="mt-6 text-neutral-500 text-lg">
              Be the first to know about new releases, exclusive drops, and special offers.
            </p>

            <form onSubmit={handleSubscribe} className="mt-10 flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="newsletter-email-input"
                className="flex-1 bg-neutral-100 border border-neutral-200 px-6 py-4 text-black focus:outline-none focus:border-black transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                className="btn-primary whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>

            {subscribeStatus === 'success' && (
              <p className="mt-4 text-green-600">Successfully subscribed!</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="mt-4 text-red-500">Something went wrong. Please try again.</p>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
